import { useLanguage } from "@/contexts/LanguageContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { FileText, Mail } from "lucide-react";
import { legalTranslations } from "@/i18n/legalTranslations";

export default function TermsOfServicePage({ user, onLogout, refreshUserData }) {
  const { language } = useLanguage();
  const t = legalTranslations[language]?.terms || legalTranslations.fr.terms;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar user={user} onLogout={onLogout} refreshUserData={refreshUserData} />

      <div className="h-20"></div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center p-3 bg-brand-100 rounded-full mb-4">
            <FileText className="h-8 w-8 text-brand-600" />
          </div>
          <h1 className="text-3xl font-bold text-slate-800">{t.pageTitle}</h1>
          <p className="text-slate-600 mt-2">{t.lastUpdated}</p>
        </div>

        {/* Content */}
        <div className="prose prose-slate max-w-none bg-white rounded-xl shadow-sm p-8">
          
          {/* Introduction */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-slate-800 mb-4">{t.intro.title}</h2>
            <p>{t.intro.p1}</p>
            <p className="mt-4">{t.intro.p2}</p>
          </section>

          {/* Nature du service offert */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-slate-800 mb-4">{t.service.title}</h2>
            
            <h3 className="text-lg font-semibold mt-4 mb-2">{t.service.intermediary.title}</h3>
            <p>{t.service.intermediary.p1}</p>
            <p className="mt-2">{t.service.intermediary.p2} <strong>{t.service.intermediary.p3}</strong></p>

            <h3 className="text-lg font-semibold mt-4 mb-2">{t.service.cancellation.title}</h3>
            <p>{t.service.cancellation.p1}</p>

            <h3 className="text-lg font-semibold mt-4 mb-2">{t.service.role.title}</h3>
            <p>{t.service.role.p1}</p>

            <h3 className="text-lg font-semibold mt-4 mb-2">{t.service.limitation.title}</h3>
            <p>{t.service.limitation.p1}</p>
          </section>

          {/* Eligibility */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-slate-800 mb-4">{t.eligibility.title}</h2>
            <p>{t.eligibility.p1}</p>
          </section>

          {/* Fees */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-slate-800 mb-4">{t.fees.title}</h2>
            <p>{t.fees.p1}</p>
            <p className="mt-2">{t.fees.p2}</p>
          </section>

          {/* Buyer Responsibilities */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-slate-800 mb-4">{t.buyer.title}</h2>
            <p>{t.buyer.p1}</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              {t.buyer.items.map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
          </section>

          {/* Seller Responsibilities */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-slate-800 mb-4">{t.seller.title}</h2>
            <p>{t.seller.p1}</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              {t.seller.items.map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
          </section>

          {/* Prohibited Conduct */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-slate-800 mb-4">{t.prohibited.title}</h2>
            <p>{t.prohibited.p1}</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              {t.prohibited.items.map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
          </section>

          {/* Dispute Resolution */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-slate-800 mb-4">{t.disputes.title}</h2>
            <p>{t.disputes.p1}</p>
            <p className="mt-2">{t.disputes.p2}</p>
          </section>

          {/* Limitation of Liability */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-slate-800 mb-4">{t.liability.title}</h2>
            <p>{t.liability.p1}</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              {t.liability.items.map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
          </section>

          {/* Contact */}
          <section className="mb-4">
            <h2 className="text-xl font-bold text-slate-800 mb-4">{t.contact.title}</h2>
            <p>{t.contact.p1}</p>
            <div className="mt-4 p-4 bg-slate-50 rounded-lg flex items-center gap-3">
              <Mail className="h-5 w-5 text-brand-600" />
              <a href="mailto:info@transfertdebillets.ca" className="text-brand-600 hover:underline font-medium">
                info@transfertdebillets.ca
              </a>
            </div>
          </section>

        </div>
      </div>

      <Footer />
    </div>
  );
}
